import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

// =============================================================================
// useIdleTimeout — déconnexion automatique après inactivité (OBS-11).
// -----------------------------------------------------------------------------
// Sécurise les sessions laissées ouvertes sur un poste partagé. Au-delà du
// délai d'inactivité, la session Supabase est fermée et l'utilisateur est
// redirigé vers /login?timeout=1 (la page de connexion affiche un message).
// =============================================================================

/** Délai d'inactivité avant déconnexion automatique (30 minutes). */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const IDLE_WARNING_MS = 2 * 60 * 1000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

export function useIdleTimeout(enabled = true) {
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef(false);
  const [warning, setWarning] = useState(false);
  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } finally {
      navigate("/login?timeout=1", { replace: true });
    }
  }, [navigate]);
  const extend = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warningRef.current = false;
    setWarning(false);
    warningTimer.current = setTimeout(() => {
      warningRef.current = true;
      setWarning(true);
    }, IDLE_TIMEOUT_MS - IDLE_WARNING_MS);
    timerRef.current = setTimeout(() => void logout(), IDLE_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      if (!warningRef.current) extend();
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [enabled, extend]);
  return { warning, extend, logout };
}
